import { Grid } from '@material-ui/core';
import HelpIcon from '@material-ui/icons/Help';
import React, { useState } from 'react';
import { pushEmeraldModal } from '../common/emerald-modal-manager/emerald-modal-manager';
import { store } from '../common/redux/configure-store';
import {
  ForcedExchangeModalButtonsProps,
  getForcedExchangeModalButtons,
} from './components/forced-exchange-buttons';
import ForcedExchangeList from './components/forced-exchange-list';
import { styles } from './force-exchange-modal-styles';
import {
  bundleOnOverride,
  getForcedExchangeTotal,
  handleSelectThreshold,
  isOverrideBtnDisabled,
} from './business-logic/handle-forced-exchange';
import { IReturnPolicy } from '../common/api/return-policy/return-policy-utils';
import EmeraldDialog from 'src/common/components/emerald-dialog/emerald-dialog';
import { useTranslation } from 'react-i18next';

export interface ForcedExchangeOverride {
  sequenceNumbers: (string | null)[];
  thresholdOverride: boolean;
  returnPolicy?: IReturnPolicy;
}

export interface ForceExchangeModalProps {
  policy: IReturnPolicy;
  onOverride: (feData: ForcedExchangeOverride) => void;
  onCancel: () => void;
  onSaleMode: () => void;
}

export const showForcedExchangeAmountModal = (props: ForceExchangeModalProps): void => {
  pushEmeraldModal(<ForceExchangeModal {...props} />);
};

const ForceExchangeModal: React.FC<ForceExchangeModalProps> = (
  props: ForceExchangeModalProps,
): JSX.Element => {
  const classes = styles();
  const { policy, onOverride, onCancel, onSaleMode } = props;
  const [selectAll, setSelectAll] = useState(false);
  const [thresholdSelected, setthresholdSelected] = useState(false);
  const forcedExchangeDetails = policy.data;
  const [selectedLines, setSelectedLines] = useState<number[]>([]);
  const isReturnMode = !!store.getState().posMode.returnMode.returnType;
  const { forcedExchangeLines, forcedExchangeTotal, transaction } = forcedExchangeDetails;
  const thresholdOverride = transaction?.managerOverride;
  const totalLines = forcedExchangeLines.length;
  const hasExchangeLines = totalLines > 0;
  const isMangerBtnDisabled =
    forcedExchangeLines.filter(l => l.managerOverride === true).length === 0 && !thresholdOverride;
  const { t } = useTranslation();
  const selectAllMsg = t('ForcedExchange_SelectAll');
  const deselectMsg = t('ForcedExchange_DeselectAll');

  const handleSelectItem = (index: number): void => {
    if (isMangerBtnDisabled) {
      return;
    }
    let newSelectedLines = [...selectedLines];
    if (selectedLines.includes(index)) {
      newSelectedLines = newSelectedLines.filter(i => {
        return i !== index;
      });
    } else {
      newSelectedLines.push(index);
    }
    if (totalLines === newSelectedLines.length) {
      setSelectAll(true);
    } else {
      setSelectAll(false);
    }
    setSelectedLines([...newSelectedLines]);
  };

  const onSelect = (): void => {
    if (isMangerBtnDisabled && !thresholdOverride) {
      return;
    }
    if (thresholdOverride) {
      handleSelectThreshold(thresholdSelected, setthresholdSelected, setSelectAll);
    } else {
      if (!selectAll) {
        setSelectedLines(forcedExchangeLines.map((e, i) => i));
      } else {
        setSelectedLines([]);
      }
      setSelectAll(!selectAll);
    }
  };

  const triButtonModalBottomProps: ForcedExchangeModalButtonsProps = {
    closeButton: {
      label: t('ButtonCancelStyle_Cancel'),
      onClick: onCancel,
    },
    selectButton: {
      label: !isMangerBtnDisabled ? (selectAll ? deselectMsg : selectAllMsg) : selectAllMsg,
      onClick: onSelect,
      disabled: isMangerBtnDisabled && !thresholdOverride,
    },
    overrideButton: {
      label: t('PriceOverride_Override'),
      onClick: () =>
        bundleOnOverride(onOverride, forcedExchangeLines, selectedLines, policy, thresholdOverride),
      disabled: isOverrideBtnDisabled(isMangerBtnDisabled, selectedLines, thresholdSelected),
    },
    saleModeButton: {
      label: t('TransactionReturn_ForceExchange_GoTOSaleMode'),
      onClick: onSaleMode,
      disabled: !isReturnMode,
    },
  };

  return (
    <EmeraldDialog
      open
      fullWidth={true}
      className={classes.component}
      classes={{
        paper: classes.dialogPaper,
      }}
      data-testid='force-exchange-dialog'
    >
      <Grid item xs={12}>
        <Grid
          className={classes.header}
          container
          direction='row'
          justifyContent='space-between'
          alignItems='center'
        >
          <Grid item wrap={'nowrap'}>
            <span className={classes.spacing} data-testid='force-exchange-dialog-title'>
              {t('ForcedExchangeView_Title')}
            </span>
          </Grid>
          <Grid item>
            <HelpIcon classes={{ root: `${classes.icon} ${classes.decision}` }} />
          </Grid>
        </Grid>
        <div className={classes.title} data-testid='force-exchange-dialog-view-message'>
          {t('ForcedExchangeView_Message')}
        </div>
        <Grid
          item
          style={{ textAlign: 'center' }}
          data-testid='force-exchange-dialog-exchange-total'
          wrap={'nowrap'}
        >
          {' '}
          ${getForcedExchangeTotal(forcedExchangeTotal)}
        </Grid>
        <ForcedExchangeList
          handleSelection={
            !hasExchangeLines
              ? () => handleSelectThreshold(thresholdSelected, setthresholdSelected, setSelectAll)
              : handleSelectItem
          }
          selectedLines={selectedLines}
          forcedExchangeLines={forcedExchangeLines}
          transaction={transaction}
          thresholdOverride={thresholdOverride}
          thresholdSelected={thresholdSelected}
          hasExchangeLines={hasExchangeLines}
        />
        {getForcedExchangeModalButtons(triButtonModalBottomProps)}
      </Grid>
    </EmeraldDialog>
  );
};

export default ForceExchangeModal;
